import {createAsyncThunk} from "@reduxjs/toolkit";
import type {CarAuction, CarItem} from "@/types";
import {supabase} from "@/utils/supabase.ts";
import {addAuction, type RootState} from "@/store";
import {isUndefined, omitBy, pick} from "lodash";
import {generateId, keysToSnakeCase, uploadToR2, vehiclesTableColumns} from "@/utils";
import imageCompression from "browser-image-compression";

type UploadedImageInput = {
    file?: File;
    preview?: string;
    isCover?: boolean;
};

export interface CreateVehicleProgressEvent {
    status: "idle" | "running" | "success" | "error";
    progress: number;
    step: string;
    detail?: string;
}


type CreateVehicleProgressListener = (event: CreateVehicleProgressEvent) => void;

const listeners = new Set<CreateVehicleProgressListener>();

export const subscribeCreateVehicleProgress = (listener: CreateVehicleProgressListener) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener)
    };
};

const emit = (event: CreateVehicleProgressEvent) => {
    console.log("[createVehicleThunk][progress]", event);
    listeners.forEach((l) => l(event));
};



const cleanForDB = (data: any) =>
    omitBy(pick(data, vehiclesTableColumns), isUndefined);

async function uploadImage(
    input: UploadedImageInput,
    path: string,
    index: number
) {
    if (input.file instanceof File) {
        const compressedFile = await imageCompression(input.file, {
            fileType: 'image/webp',
            maxSizeMB: 1,
            maxWidthOrHeight: 1280,
            useWebWorker: true,
        })
        const filename = `${Date.now()}_${index}.webp`;

        return {
            url: await uploadToR2("vehicle-media", `${path}/${filename}`, compressedFile, "image/webp"),
            index,
            isCover: input.isCover,
        };
    }

    if (input.preview) {
        return {
            url: input.preview,
            index,
            isCover: input.isCover,
        };
    }

    return null;
}

export const createVehicleThunk = createAsyncThunk(
    "listing/createVehicleThunk",
    async (
        { details, images }: { details: Partial<CarItem | CarAuction>; images: UploadedImageInput[] },
        { getState, dispatch, rejectWithValue }
    ) => {
        try {
            emit({
                status: "running",
                progress: 5,
                step: "Preparing",
                detail: "Validating session and input",
            });

            // Auth
            const { authentication } = getState() as RootState;
            const stateUserId = authentication.dealerUser?.id;
            const sellerId = authentication.user?.id;

            const { data, error } = await supabase.auth.getUser();
            if (error) return rejectWithValue(error.message);

            const authUserId = data.user?.id;
            if (!authUserId) {
                return rejectWithValue("Session expired. Please log in again.");
            }

            if (stateUserId && stateUserId !== authUserId) {
                return rejectWithValue("Auth mismatch detected.");
            }

            if (!sellerId) {
                return rejectWithValue("No dealership profile found.");
            }

            emit({
                status: "running",
                progress: 15,
                step: "Generating ID",
                detail: "Creating vehicle identifier",
            });

            const carId = generateId();
            const path = `${sellerId}/${carId}`;

            // Upload Images
            emit({
                status: "running",
                progress: 25,
                step: "Uploading images",
                detail: `Uploading ${images.length} images`,
            });

            const uploaded = await Promise.all(
                images.map((img, i) => uploadImage(img, path, i))
            );

            const sortedImages = uploaded
                .filter(Boolean)
                .sort((a, b) => a!.index - b!.index)
                .map((i) => i!.url);

            emit({
                status: "running",
                progress: 50,
                step: "Processing data",
                detail: "Formatting vehicle payload",
            });

            //  Prepare Data
            const formatted = keysToSnakeCase({
                ...details,
                id: carId,
                images: sortedImages,
                sellerId: sellerId,
                active: 'draft'
            });

            const vehicle = cleanForDB(formatted);

            // Insert Vehicle
            emit({
                status: "running",
                progress: 65,
                step: "Saving vehicle",
                detail: "Inserting into database",
            });

            const { data: vehicleData, error: vehicleError } = await supabase
                .from("vehicles")
                .insert(vehicle)
                .select("id")
                .single();

            if (vehicleError || !vehicleData?.id) {
                return rejectWithValue(vehicleError?.message || "Vehicle insert failed");
            }

            const vehicleId = vehicleData.id as string;

            //Listing / Auction
            if (details.type === "auction") {
                emit({
                    status: "running",
                    progress: 80,
                    step: "Creating auction",
                    detail: "Setting up auction data",
                });

                const { data: auctionData, error } = await supabase
                    .from("auctions")
                    .insert({
                        vehicle_id: vehicleId,
                        seller_id: sellerId,
                        starting_bid: details.startingBid ?? 0,
                        current_bid: details.startingBid ?? 0,
                        ending: details.ending
                            ? new Date(String(details.ending)).toISOString()
                            : null,
                        status: "draft",
                    })
                    .select("id")
                    .single();

                if (error) return rejectWithValue(error.message);

                dispatch(addAuction(details as CarAuction));

                emit({
                    status: "success",
                    progress: 100,
                    step: "Completed",
                    detail: "Auction created successfully",
                });

                return {
                    vehicleId,
                    type: "auction",
                    auctionId: auctionData.id,
                };
            }

            if (details.type === "listing") {
                emit({
                    status: "running",
                    progress: 80,
                    step: "Creating listing",
                    detail: "Setting up listing data",
                });

                const { data: listingData, error } = await supabase
                    .from("listings")
                    .insert({
                        vehicle_id: vehicleId,
                        price: details.price ?? 0,
                        negotiable: details.negotiable ?? false,
                        status: details.active ?? "active",
                    })
                    .select("id")
                    .single();

                if (error) return rejectWithValue(error.message);

                emit({
                    status: "success",
                    progress: 100,
                    step: "Completed",
                    detail: "Listing created successfully",
                });

                return {
                    vehicleId,
                    type: "listing",
                    listingId: listingData.id,
                };
            }

            return rejectWithValue("Invalid type");
        } catch (err) {
            emit({
                status: "error",
                progress: 100,
                step: "Failed",
                detail: err instanceof Error ? err.message : "Unknown error",
            });

            return rejectWithValue("Failed to create vehicle");
        }
    }
);
